import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CommandService } from '@helios/commands'
import {
  ERROR_INTERCEPTOR_BYPASS,
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { EditTransactionMode } from '../edit-transaction/edit-transaction.projection'
import {
  TRANSACTION_SAVE_COPY,
  TRANSACTION_SAVE_VALIDATION_COPY,
} from '../models/transaction-action.model'
import {
  TransactionSaveRequest,
  TransactionSaveResponse,
} from '../models/transaction-save.model'
import { readValidations } from '../utils/read-validations'
import { detailOf } from './http-trade-read.service'
import {
  PANEL_NAME,
  WORKFLOW_ERROR_STATUSES,
} from './http-transaction-actions.service'
import { TransactionSaveService } from './transaction-save.service'

/**
 * The save endpoint (helios !518). Resolved against `<base href="/helios/">` like every other
 * flex-transactions call, and behind the same LOCAL-ONLY proxy entry.
 */
export const SAVE_URL = 'helios-flex-transactions/transactions/save'

/** The verb each dialog mode is stamped with in the commands drawer. */
export const SAVE_ACTION: Readonly<Record<EditTransactionMode, string>> = {
  create: 'Create Transaction',
  edit: 'Edit Transaction',
  post: 'Post Transaction',
}

/**
 * A 200 whose body is not the array the contract promises — an object, or an HTML page a proxy
 * answered with. The request DID reach a server, so it may have created commands: it is reported
 * like a 5xx, with the advice to check the commands drawer before retrying.
 */
export class SaveResponseShapeError extends Error {
  constructor(readonly body: unknown) {
    super('The save answered something other than a list of commands')
    this.name = 'SaveResponseShapeError'
    Object.setPrototypeOf(this, SaveResponseShapeError.prototype)
  }
}

/**
 * Live implementation of the save seam, with the same context recipe as
 * `HttpTransactionActionsService` — and for the same reasons, documented there:
 *
 *  - `HANDLE_ERROR_AND_THROW`, without which the global `ErrorInterceptor` completes the observable
 *    WITHOUT emitting and the dialog waits on its overlay forever;
 *  - `ERROR_INTERCEPTOR_BYPASS` with a per-request COPY of `[400, 409]`: a rejected save is the
 *    user's to fix, not a defect for the error drawer (and the copy, because the token holds the
 *    array by reference and the exported constant is shared);
 *  - `HELIOS_COMMAND_HTTP_TOKEN`, which drives the commands drawer's loading state;
 *  - `generateTemporaryHeaders`, which attributes the command to this panel and this verb.
 *
 * `helios-session-id` is attached by the SessionInterceptor, never here.
 */
@Injectable()
export class HttpTransactionSaveService extends TransactionSaveService {
  constructor(
    private readonly http: HttpClient,
    private readonly commandService: CommandService,
  ) {
    super()
  }

  save(
    request: TransactionSaveRequest,
    mode: EditTransactionMode = 'edit',
  ): Observable<TransactionSaveResponse> {
    return this.http
      .post<unknown>(SAVE_URL, request, {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          SAVE_ACTION[mode],
        ),
        context: new HttpContext()
          .set(ERROR_INTERCEPTOR_BYPASS, [...WORKFLOW_ERROR_STATUSES])
          .set(HANDLE_ERROR_AND_THROW, true)
          .set(HELIOS_COMMAND_HTTP_TOKEN, true),
      })
      .pipe(
        map(body => {
          if (!Array.isArray(body)) throw new SaveResponseShapeError(body)
          return body as TransactionSaveResponse
        }),
      )
  }
}

/** What a failed save says, and the detail the dialog's error box shows beneath it. */
export interface SaveFailure {
  message: string
  /** Set for a failure that may have reached the server — the error box's header. */
  header?: string
  /** For `hs-dialog-error-details`; set alongside {@link header}. */
  details?: unknown
}

const generic = (): string =>
  `${TRANSACTION_SAVE_COPY.generic} ${TRANSACTION_SAVE_COPY.checkDrawer}`

/**
 * The copy a failed save is shown with (BC-8), shared by the single and bulk dialogs.
 *
 *  - 400 with a validation array — each code's copy (`TRANSACTION_SAVE_VALIDATION_COPY`), falling
 *    back to the server's trimmed message for a code this build has no copy for. A 400 with no
 *    array (the commands interceptor's body-less one, for a session with approvals pending) never
 *    reached the endpoint, so it reads as the generic failure.
 *  - 409 — the record changed concurrently; 403 — no permission.
 *  - 5xx, status 0, or a 200 the client could not read — the generic copy, ending with the advice
 *    to check the commands drawer, since the command may exist; with the detail for the error box.
 */
export function saveFailureMessage(error: unknown): SaveFailure {
  if (error instanceof SaveResponseShapeError) {
    return { message: generic(), header: 'Save failed', details: error.body }
  }
  if (!(error instanceof HttpErrorResponse)) {
    return {
      message: generic(),
      header: 'Save failed',
      details: detailOf(error, SAVE_URL),
    }
  }
  if (error.status === 400) {
    const validations = readValidations(error.error)
    if (validations.length) {
      const messages = validations.map(
        validation =>
          TRANSACTION_SAVE_VALIDATION_COPY[validation.validationId] ??
          (validation.message?.trim() || TRANSACTION_SAVE_COPY.generic),
      )
      return { message: [...new Set(messages)].join(' ') }
    }
    return { message: TRANSACTION_SAVE_COPY.generic }
  }
  if (error.status === 409) return { message: TRANSACTION_SAVE_COPY.conflict }
  if (error.status === 403) return { message: TRANSACTION_SAVE_COPY.forbidden }
  return {
    message: generic(),
    header: 'Save failed',
    details: detailOf(error, SAVE_URL),
  }
}
